/**
 * Product documentation viewer (FR-HELP-05, AC-HELP-004).
 *
 * One component serves two surfaces:
 *   - `variant="overlay"` — the Help Center's Docs tab;
 *   - `variant="page"`    — the standalone `/app/docs/:slug` route, where the
 *     left pane is resizable and the width persists per device.
 *
 * Everything it renders comes from the Markdown bundled at build time
 * (`web/src/docs/*.md`), parsed once in `lib/help-content` — no network call, so
 * a doc paints well inside the 500 ms budget and works offline.
 *
 * Deep links: the caller supplies `slug` and `sectionId`; the viewer scrolls the
 * matching heading into view and moves focus to it, so
 * `/app/docs/play-entry#conflict-resolution` lands a keyboard user on the right
 * heading rather than at the top of the page.
 *
 * The ToC is filtered to the reader's persona — a Fanatic sees Fanatic-scoped
 * docs only — unless the "all roles" toggle is on.
 */
import { useEffect, useMemo, useRef } from 'react';

import { MarkdownView } from '../../components/markdown-view';
import { TextField } from '../../components/text-field';
import { countMatches, searchDocs } from '../../lib/docs-search';
import { docsForPersona, findDoc, groupDocsByCategory, type DocPage } from '../../lib/help-content';
import { PERSONA_LABELS, type Persona } from '../../lib/personas';

export interface DocsViewerProps {
  /** `null` when the session's persona is unknown — least-privilege scope. */
  persona: Persona | null;
  /** Slug of the open doc; falls back to the first visible doc. */
  slug: string | null;
  /** Heading anchor to scroll to, without the `#`. */
  sectionId: string | null;
  search: string;
  isAllRoles: boolean;
  variant?: 'overlay' | 'page';
  onSelect: (slug: string, sectionId?: string | null) => void;
  onSearchChange: (term: string) => void;
  onAllRolesChange: (isAllRoles: boolean) => void;
  /** Rendered under the ToC — e.g. the overlay's "open full docs" link. */
  tocFooter?: React.ReactNode;
}

export function DocsViewer({
  persona,
  slug,
  sectionId,
  search,
  isAllRoles,
  variant = 'overlay',
  onSelect,
  onSearchChange,
  onAllRolesChange,
  tocFooter,
}: DocsViewerProps): JSX.Element {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const term = search.trim();

  const visibleDocs = useMemo(() => docsForPersona(persona, isAllRoles), [isAllRoles, persona]);
  const hits = useMemo(() => searchDocs(term, persona, isAllRoles), [isAllRoles, persona, term]);
  const matchedSlugs = useMemo(() => new Set(hits.map((hit) => hit.slug)), [hits]);

  // Search filters the ToC; an empty term leaves the whole persona-scoped list.
  const tocDocs =
    term === '' ? visibleDocs : visibleDocs.filter((doc) => matchedSlugs.has(doc.slug));
  const requested = findDoc(slug ?? undefined);
  const active: DocPage | undefined =
    requested !== undefined && visibleDocs.includes(requested)
      ? requested
      : (tocDocs[0] ?? visibleDocs[0]);

  // Deep-link / ToC-click scroll. Runs after the body renders for the active
  // doc, so the anchor exists by the time we look it up.
  useEffect(() => {
    const container = bodyRef.current;
    if (container === null || active === undefined) return;
    if (sectionId === null || sectionId === '') {
      container.scrollTo?.({ top: 0 });
      return;
    }
    const target = container.querySelector<HTMLElement>(`#${CSS.escape(sectionId)}`);
    if (target === null) return;
    target.scrollIntoView({ block: 'start', behavior: 'auto' });
    target.focus({ preventScroll: true });
  }, [active, sectionId]);

  const isSlugMissing = slug !== null && slug !== '' && requested === undefined;

  return (
    <div className={`docs docs--${variant}`}>
      <aside className="docs__toc" aria-label="Documentation contents">
        <TextField
          label="Search docs"
          type="search"
          value={search}
          placeholder="conflict resolution, offline, follow…"
          onChange={(event) => onSearchChange(event.target.value)}
        />
        <label className="docs__toggle" htmlFor="docs-all-roles">
          <input
            id="docs-all-roles"
            type="checkbox"
            checked={isAllRoles}
            onChange={(event) => onAllRolesChange(event.target.checked)}
          />
          <span>
            All roles
            <span className="field__hint">
              {isAllRoles || persona === null
                ? 'Every bundled page.'
                : `Pages for ${PERSONA_LABELS[persona]}.`}
            </span>
          </span>
        </label>

        {tocDocs.length === 0 ? (
          <p className="muted" role="status">
            {term === ''
              ? 'No documentation is bundled for this role yet.'
              : `No page matches “${term}”.`}
          </p>
        ) : null}

        {groupDocsByCategory(tocDocs).map(([category, pages]) => (
          <section key={category} className="docs__group">
            <h2 className="docs__group-title">{category}</h2>
            <ul className="docs__list">
              {pages.map((page) => {
                const isActive = page.slug === active?.slug;
                const matches = countMatches(page, term);
                return (
                  <li key={page.slug}>
                    <button
                      type="button"
                      className={`docs__link${isActive ? ' docs__link--active' : ''}`}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() => onSelect(page.slug, null)}
                    >
                      <span>{page.title}</span>
                      {matches > 0 ? <span className="badge">{matches}</span> : null}
                    </button>
                    {isActive ? (
                      <ul className="docs__sections">
                        {page.sections
                          .filter((section) => section.level > 1 && section.level <= 3)
                          .map((section) => (
                            <li key={section.id}>
                              <a
                                className={`docs__section docs__section--l${section.level}`}
                                href={`#${section.id}`}
                                aria-current={section.id === sectionId ? 'true' : undefined}
                                onClick={(event) => {
                                  event.preventDefault();
                                  onSelect(page.slug, section.id);
                                }}
                              >
                                {section.title}
                              </a>
                            </li>
                          ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
        {tocFooter}
      </aside>

      <div className="docs__body" ref={bodyRef} tabIndex={-1}>
        {isSlugMissing ? (
          <div className="banner banner--warning" role="status">
            <span className="banner__title">Page not found</span>
            <span>
              No documentation page is called “{slug}”
              {active === undefined ? '.' : ` — showing ${active.title} instead.`}
            </span>
          </div>
        ) : null}

        {term !== '' && hits.length > 0 ? (
          <div className="docs__hits">
            <h2 className="docs__group-title">
              {hits.length} {hits.length === 1 ? 'match' : 'matches'} for “{term}”
            </h2>
            <ul className="docs__list">
              {hits.slice(0, 8).map((hit) => (
                <li key={`${hit.slug}-${hit.sectionId ?? 'top'}`}>
                  <button
                    type="button"
                    className="docs__hit"
                    onClick={() => onSelect(hit.slug, hit.sectionId)}
                  >
                    <span className="docs__hit-title">
                      {hit.title}
                      {hit.sectionTitle === null ? '' : ` › ${hit.sectionTitle}`}
                    </span>
                    <span className="muted">{hit.excerpt}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {active === undefined ? (
          <div className="help-empty" role="status">
            <p>No documentation is available for your role in this build.</p>
          </div>
        ) : (
          <article className="docs__article" aria-label={active.title}>
            <MarkdownView source={active.source} highlight={term} />
          </article>
        )}
      </div>
    </div>
  );
}
